import type { AuthScope, ModelTreePermission } from "apis/dataV2/authTypes";
import type { DeviationProjectConfig } from "apis/dataV2/deviationTypes";
import type { Permission } from "apis/dataV2/permissions";
import type { ProjectInfo } from "apis/dataV2/projectTypes";

/**
 * novoweb's schema for a scene's host data: the app data a full sync captures into the scene's
 * offline directory, read back offline. The SDK stores named JSON blobs;
 * the names ({@link hostDataNames}) and shapes are novoweb's, spelled only here. The sync commits a
 * capture whole, but readers read blob by blob, so every reader checks {@link HostDataMeta} first.
 */

/**
 * Bumped when a blob's shape changes incompatibly, or a blob a reader needs is added: a capture
 * of another version reads as none. 2: `scene/coordinateSystem`.
 */
export const HOST_DATA_SCHEMA_VERSION = 2;

export type BookmarkScope = "public" | "personal";

export type HostDataMeta = {
    readonly schemaVersion: number;
    /** The scene the capture is of: the project scene, whose id the data-v2 requests carry. */
    readonly projectId: string;
    /**
     * The scene version the capture is of: its scene index's `offline.manifest`, as the server
     * served it while capturing. Object ids in bookmark states and group id sets are valid for
     * this version only, so the capture is read only while the viewer has it loaded.
     */
    readonly manifestId: string;
    /** ISO date. */
    readonly capturedAt: string;
    /** Which bookmarks were captured: only those the user could read, and personal ones only for a user. */
    readonly bookmarks: {
        /** Whether the user could read bookmarks (`bookmark:read`). Without it, both scopes read empty. */
        readonly captured: boolean;
        /** The user whose personal bookmarks were captured. Anyone else reads the scope as empty. */
        readonly personalUser: string | null;
    };
};

/**
 * The scene's data as `GET /api/scenes/{id}` returns it: what `dataApi.loadScene` reads,
 * before it adds its object DB and fills in group ids. Title, object groups and custom
 * properties are part of it.
 */
export type RawSceneConfig = {
    readonly url: string;
    readonly title?: string;
    readonly organization?: string;
    readonly projectId?: string;
    readonly objectGroups?: readonly { readonly id?: string; readonly ids?: readonly number[] }[];
    readonly customProperties?: unknown;
    readonly [key: string]: unknown;
};

/** The scene index, as far as a capture reads it. */
export type SceneIndex = { readonly offline?: { readonly manifest?: unknown } };

/** A scene-open response, with what it was asked for: the scene open matches the two. */
export type Snapshot<Args, Data> = { readonly args: Args; readonly data: Data };

/**
 * The project's coordinate system as `https://epsg.io/{epsg}.wkt` and `.proj4` answer it: the
 * text, or `null` where the answer wasn't OK. The viewer picks one of the two, as it does online.
 */
export type CoordinateSystemSnapshot = Snapshot<{ epsg: string }, { wkt: string | null; proj4: string | null }>;

/** `POST /roles/check-permissions` for every permission: those granted. */
export type PermissionsSnapshot = Snapshot<{ scope: AuthScope }, Permission[]>;

/** The scene's committed `deviations.json` asset, or `null` if it had none. */
export type CommittedDeviations = { readonly config: DeviationProjectConfig | null };

/** `null` data, here and in other blobs, is an empty answer, as the viewer's RTK Query reads one. */
export type ProjectSnapshot = Snapshot<{ projectId: string }, ProjectInfo | null>;
export type ModelTreePermissionsSnapshot = Snapshot<{ projectId: string }, ModelTreePermission[] | null>;

/**
 * An id as one segment of a blob name. The SDK refuses a segment that is empty, `.` or `..`,
 * holds `/`, `\` or a control character, or ends in `.partial`; encoding the id, dots
 * included, rules all of those out but the empty one.
 */
export function nameSegment(id: string): string {
    return encodeURIComponent(id).replace(/\./g, "%2E");
}

export const hostDataNames = {
    meta: "meta",
    sceneConfig: "scene/config",
    project: "scene/project",
    modelTreePermissions: "scene/modelTreePermissions",
    permissions: "scene/permissions",
    coordinateSystem: "scene/coordinateSystem",
    bookmarkCatalog: (scope: BookmarkScope) => `bookmarks/${scope}/catalog`,
    bookmarkState: (scope: BookmarkScope, bookmarkId: string) => `bookmarks/${scope}/state/${nameSegment(bookmarkId)}`,
    groupIds: (groupId: string) => `groups/${nameSegment(groupId)}/ids`,
    deviationProfiles: "deviations/profiles",
    committedDeviations: "deviations/committed",
} as const;
